import { useState, useEffect } from 'react';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon, getProducts, getSales, getCustomers, updateCustomer } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Ticket, Plus, Pencil, Trash2, Calendar, Package, Percent, Banknote, Gift, Users } from 'lucide-react';

const COUPON_TYPES = [
  { value: 'percentage', label: '割引率（%）', icon: Percent },
  { value: 'fixed_amount', label: '固定金額割引', icon: Banknote },
  { value: 'free_sample', label: '無料サンプル', icon: Gift },
];

export const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [selectedCouponForAssign, setSelectedCouponForAssign] = useState(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    coupon_type: 'percentage',
    value: '',
    expiration_date: '',
    is_single_use: false,
    target_product_id: '',
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [couponsRes, productsRes, salesRes, customersRes] = await Promise.all([
        getCoupons(),
        getProducts(),
        getSales(),
        getCustomers(),
      ]);
      setCoupons(couponsRes.data);
      setProducts(productsRes.data);
      setSales(salesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      coupon_type: 'percentage',
      value: '',
      expiration_date: '',
      is_single_use: false,
      target_product_id: '',
      is_active: true,
    });
    setEditingCoupon(null);
  };

  const handleOpenDialog = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        name: coupon.name,
        coupon_type: coupon.coupon_type,
        value: coupon.value.toString(),
        expiration_date: coupon.expiration_date || '',
        is_single_use: coupon.is_single_use,
        target_product_id: coupon.target_product_id || '',
        is_active: coupon.is_active,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.value) {
      toast.error('クーポン名と値は必須です');
      return;
    }

    try {
      const data = {
        ...formData,
        value: parseFloat(formData.value),
        target_product_id: formData.target_product_id || null,
        expiration_date: formData.expiration_date || null,
      };

      if (editingCoupon) {
        await updateCoupon(editingCoupon.id, data);
        toast.success('クーポンを更新しました');
      } else {
        await createCoupon(data);
        toast.success('クーポンを作成しました');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('保存に失敗しました');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('このクーポンを削除しますか？')) return;
    try {
      await deleteCoupon(id);
      toast.success('クーポンを削除しました');
      fetchData();
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  // 顧客割り当てダイアログを開く
  const handleOpenAssignDialog = (coupon) => {
    setSelectedCouponForAssign(coupon);
    // 現在このクーポンを持っている顧客を選択状態にする
    const currentAssignedIds = customers
      .filter(c => (c.available_coupons || []).includes(coupon.id))
      .map(c => c.id);
    setSelectedCustomerIds(currentAssignedIds);
    setAssignDialogOpen(true);
  };

  // 顧客へのクーポン割り当てを保存
  const handleSaveAssignments = async () => {
    if (!selectedCouponForAssign) return;

    try {
      // 各顧客のavailable_couponsを更新
      const updates = customers.map(async (customer) => {
        const currentCoupons = customer.available_coupons || [];
        const hasCoupon = currentCoupons.includes(selectedCouponForAssign.id);
        const shouldHaveCoupon = selectedCustomerIds.includes(customer.id);

        if (hasCoupon !== shouldHaveCoupon) {
          let newCoupons;
          if (shouldHaveCoupon) {
            newCoupons = [...currentCoupons, selectedCouponForAssign.id];
          } else {
            newCoupons = currentCoupons.filter(id => id !== selectedCouponForAssign.id);
          }
          await updateCustomer(customer.id, { available_coupons: newCoupons });
        }
      });

      await Promise.all(updates);
      toast.success('顧客への割り当てを更新しました');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const toggleCustomer = (customerId) => {
    if (selectedCustomerIds.includes(customerId)) {
      setSelectedCustomerIds(selectedCustomerIds.filter(id => id !== customerId));
    } else {
      setSelectedCustomerIds([...selectedCustomerIds, customerId]);
    }
  };

  const selectAllCustomers = () => {
    setSelectedCustomerIds(customers.map(c => c.id));
  };

  const clearAllCustomers = () => {
    setSelectedCustomerIds([]);
  };

  const getCouponTypeInfo = (type) => {
    return COUPON_TYPES.find(t => t.value === type) || COUPON_TYPES[0];
  };

  const formatValue = (coupon) => {
    switch (coupon.coupon_type) {
      case 'percentage':
        return `${coupon.value}% OFF`;
      case 'fixed_amount':
        return `¥${coupon.value.toLocaleString()} OFF`;
      case 'free_sample':
        return `${coupon.value}個 無料`;
      default:
        return coupon.value;
    }
  };

  // クーポンを持っている顧客数を取得
  const getAssignedCustomerCount = (couponId) => {
    return customers.filter(c => (c.available_coupons || []).includes(couponId)).length;
  };

  const allItems = [
    ...products.map(p => ({ ...p, itemType: 'product' })),
    ...sales.filter(s => s.is_active).map(s => ({ ...s, itemType: 'sale' })),
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">読み込み中...</div>;
  }

  return (
    <div className="space-y-6" data-testid="coupons-page">
      <div className="flex justify-end">
        <Button onClick={() => handleOpenDialog()} className="bg-purple-600 hover:bg-purple-700" data-testid="add-coupon-btn">
          <Plus className="h-4 w-4 mr-2" />
          新規作成
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">クーポン一覧</CardTitle>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              クーポンがありません
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>クーポン名</TableHead>
                    <TableHead>種類</TableHead>
                    <TableHead>値</TableHead>
                    <TableHead>対象商品</TableHead>
                    <TableHead>有効期限</TableHead>
                    <TableHead>使用制限</TableHead>
                    <TableHead>配布先</TableHead>
                    <TableHead>使用回数</TableHead>
                    <TableHead>状態</TableHead>
                    <TableHead className="w-32">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.map((coupon) => {
                    const typeInfo = getCouponTypeInfo(coupon.coupon_type);
                    const TypeIcon = typeInfo.icon;
                    const assignedCount = getAssignedCustomerCount(coupon.id);
                    return (
                      <TableRow key={coupon.id} data-testid={`coupon-row-${coupon.id}`}>
                        <TableCell className="font-medium">{coupon.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <TypeIcon className="h-4 w-4 text-slate-500" />
                            <span className="text-sm">{typeInfo.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{formatValue(coupon)}</Badge>
                        </TableCell>
                        <TableCell>
                          {coupon.target_product_name ? (
                            <div className="flex items-center gap-1">
                              <Package className="h-4 w-4 text-slate-400" />
                              <span className="text-sm">{coupon.target_product_name}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">全体適用</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {coupon.expiration_date ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span className="text-sm">{coupon.expiration_date}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">無期限</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={coupon.is_single_use ? 'destructive' : 'outline'}>
                            {coupon.is_single_use ? '単回' : '無制限'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenAssignDialog(coupon)}
                            className="gap-1"
                            data-testid={`assign-coupon-${coupon.id}`}
                          >
                            <Users className="h-3 w-3" />
                            {assignedCount}社
                          </Button>
                        </TableCell>
                        <TableCell>{coupon.used_count}回</TableCell>
                        <TableCell>
                          <Badge variant={coupon.is_active ? 'default' : 'secondary'}>
                            {coupon.is_active ? '有効' : '無効'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(coupon)} data-testid={`edit-coupon-${coupon.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(coupon.id)} className="text-red-600" data-testid={`delete-coupon-${coupon.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* クーポン作成/編集ダイアログ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'クーポン編集' : '新規クーポン作成'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>クーポン名 *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="例：初回10%OFF"
                data-testid="coupon-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>種類 *</Label>
              <Select value={formData.coupon_type} onValueChange={(v) => setFormData({ ...formData, coupon_type: v })}>
                <SelectTrigger data-testid="coupon-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUPON_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                {formData.coupon_type === 'percentage' && '割引率（%） *'}
                {formData.coupon_type === 'fixed_amount' && '割引金額（円） *'}
                {formData.coupon_type === 'free_sample' && 'サンプル個数 *'}
              </Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder={formData.coupon_type === 'percentage' ? '10' : formData.coupon_type === 'fixed_amount' ? '500' : '1'}
                data-testid="coupon-value-input"
              />
            </div>

            <div className="space-y-2">
              <Label>対象商品（空欄の場合は全体適用）</Label>
              <Select value={formData.target_product_id || "none"} onValueChange={(v) => setFormData({ ...formData, target_product_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="coupon-target-select">
                  <SelectValue placeholder="全体適用" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">全体適用</SelectItem>
                  {allItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} {item.itemType === 'sale' ? '(特売)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>有効期限</Label>
              <Input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                data-testid="coupon-expiration-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>単回使用制限</Label>
              <Switch
                checked={formData.is_single_use}
                onCheckedChange={(v) => setFormData({ ...formData, is_single_use: v })}
                data-testid="coupon-single-use-switch"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>有効</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                data-testid="coupon-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="save-coupon-btn">
              {editingCoupon ? '更新' : '作成'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 顧客割り当てダイアログ */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              クーポン配布先の設定
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {selectedCouponForAssign && (
              <div className="p-3 bg-purple-50 rounded-lg mb-4">
                <p className="font-medium text-purple-900">{selectedCouponForAssign.name}</p>
                <p className="text-sm text-purple-600">{formatValue(selectedCouponForAssign)}</p>
              </div>
            )}
            <p className="text-sm text-slate-500 mb-3">
              このクーポンを配布する取引先を選択してください。
            </p>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={selectAllCustomers}>全て選択</Button>
              <Button variant="outline" size="sm" onClick={clearAllCustomers}>全て解除</Button>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto border rounded-md p-3">
              {customers.length === 0 ? (
                <p className="text-center text-slate-400 py-4">取引先がありません</p>
              ) : (
                customers.map((customer) => (
                  <div key={customer.id} className="flex items-center space-x-3 py-2 border-b last:border-b-0 hover:bg-slate-50">
                    <Checkbox
                      id={`customer-${customer.id}`}
                      checked={selectedCustomerIds.includes(customer.id)}
                      onCheckedChange={() => toggleCustomer(customer.id)}
                    />
                    <label htmlFor={`customer-${customer.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium">{customer.name}</span>
                      <span className="text-sm text-slate-500 ml-2">({customer.code})</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {selectedCustomerIds.length}社 選択中
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSaveAssignments} className="bg-purple-600 hover:bg-purple-700" data-testid="save-assignments-btn">
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
